import { Link } from 'react-router';
import {
  ArrowRight,
  Car,
  CheckCircle2,
  Megaphone,
  MessageSquare,
  Package,
  Plus,
  ShoppingBag,
  Wrench,
} from '@plataforma/ui/iconos';
import { useConsulta } from '@plataforma/web/api';
import { useSesion } from '@plataforma/web/sesion';
import { ETIQUETAS_ESTADO, fechaCorta, nombreVehiculo } from '../../formato';
import { usePuede } from '../../permisos';
import type { Lead, Pedido } from '../comercial-comun';
import type { Vehiculo } from '../tipos';

/** Las etapas del vehículo en orden, como un embudo de la subasta a la venta. */
const ETAPAS = [
  'lote_en_subasta',
  'comprado',
  'transito_aduana',
  'recibido',
  'en_reparacion',
  'en_preparacion',
  'listo',
  'publicado',
  'en_desarme',
];
const TERMINADOS = ['vendido', 'chatarra', 'desarmado'];
const PEDIDOS_ABIERTOS = ['apartado', 'confirmado', 'listo_entrega'];
const ENCARGOS_DECIDIR = ['propuesta_enviada'];
const ENCARGOS_EN_CURSO = ['aceptado', 'anticipo_pagado', 'en_puja', 'ganado', 'importando'];

interface Pendiente {
  clave: string;
  Icono: typeof Car;
  cuenta: number;
  texto: string;
  a: string;
  urgente?: boolean;
}

const saludo = () => {
  const hora = Number(
    new Intl.DateTimeFormat('es-MX', {
      hour: 'numeric',
      hourCycle: 'h23',
      timeZone: 'America/Mexico_City',
    }).format(),
  );
  return hora < 12 ? 'Buenos días' : hora < 19 ? 'Buenas tardes' : 'Buenas noches';
};

/**
 * Tablero del Admin (arquitectura 2.16): indicadores del inventario, los pendientes de cada rol
 * (solo lo que su permiso deja ver, ni siquiera se pide lo demás), el embudo por etapa y lo último
 * que se movió.
 */
export function Tablero() {
  const permitido = usePuede();
  const { usuario } = useSesion();
  const { datos: vehiculos, error } = useConsulta<Vehiculo[]>('/inventory/vehiculos');
  const leads = useConsulta<Lead[]>(
    permitido('leads.atender') ? '/commerce/leads?estado=nuevo' : null,
  );
  const pedidos = useConsulta<Pedido[]>(permitido('pedidos.ver') ? '/commerce/pedidos' : null);
  const ordenes = useConsulta<{ estado: string }[]>(
    permitido('ordenes.ver') ? '/workshop/tablero' : null,
  );
  const encargos = useConsulta<{ estado: string }[]>(
    permitido('encargos.ver') ? '/sourcing/encargos' : null,
  );

  const lista = vehiculos ?? [];
  const por = (estado: string) => lista.filter((v) => v.estado === estado).length;
  const activos = lista.filter((v) => !TERMINADOS.includes(v.estado)).length;
  const hoy = new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'full',
    timeZone: 'America/Mexico_City',
  }).format();

  const pendientes: Pendiente[] = [
    leads.datos && {
      clave: 'leads',
      Icono: MessageSquare,
      cuenta: leads.datos.length,
      texto: leads.datos.length === 1 ? 'interesado sin contestar' : 'interesados sin contestar',
      a: '/leads?estado=nuevo',
      urgente: true,
    },
    pedidos.datos && {
      clave: 'pedidos',
      Icono: Package,
      cuenta: pedidos.datos.filter((p) => PEDIDOS_ABIERTOS.includes(p.estado)).length,
      texto: 'pedidos por confirmar o entregar',
      a: '/pedidos',
      urgente: true,
    },
    encargos.datos && {
      clave: 'encargos-decidir',
      Icono: ShoppingBag,
      cuenta: encargos.datos.filter((e) => ENCARGOS_DECIDIR.includes(e.estado)).length,
      texto: 'encargos con propuesta por decidir',
      a: '/encargos',
      urgente: true,
    },
    encargos.datos && {
      clave: 'encargos-curso',
      Icono: ShoppingBag,
      cuenta: encargos.datos.filter((e) => ENCARGOS_EN_CURSO.includes(e.estado)).length,
      texto: 'encargos en curso',
      a: '/encargos',
    },
    ordenes.datos && {
      clave: 'ordenes',
      Icono: Wrench,
      cuenta: ordenes.datos.filter((o) => ['abierta', 'en_proceso'].includes(o.estado)).length,
      texto: 'órdenes de taller abiertas',
      a:
        permitido('ordenes.trabajar') && !permitido('montos.ver')
          ? '/taller/mis-tareas'
          : '/taller',
    },
    vehiculos && {
      clave: 'listos',
      Icono: Megaphone,
      cuenta: por('listo'),
      texto: 'autos listos sin publicar',
      a: '/vehiculos?estado=listo',
    },
  ].filter((p): p is Pendiente => Boolean(p));
  const conPendientes = pendientes.filter((p) => p.cuenta > 0);

  const indicadores = [
    {
      rotulo: 'Autos en inventario',
      cifra: activos,
      nota: `${por('transito_aduana')} en tránsito o aduana`,
      Icono: Car,
    },
    {
      rotulo: 'En taller o preparación',
      cifra: por('en_reparacion') + por('en_preparacion'),
      nota: `${por('recibido')} recibidos por revisar`,
      Icono: Wrench,
    },
    {
      rotulo: 'Publicados',
      cifra: por('publicado'),
      nota: `${por('listo')} listos para publicar`,
      Icono: Megaphone,
    },
    {
      rotulo: 'Vendidos',
      cifra: por('vendido'),
      nota: 'Desde que usas Siniestrados',
      Icono: CheckCircle2,
    },
  ];

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>
            {saludo()}
            {usuario?.nombre ? `, ${usuario.nombre.split(' ')[0]}` : ''}
          </h1>
          <div className="subtitulo">{hoy[0]?.toUpperCase() + hoy.slice(1)}</div>
        </div>
        <div className="relleno" />
        {permitido('vehiculos.registrar') && (
          <Link className="boton primario" to="/vehiculos/nuevo">
            <Plus size={18} aria-hidden="true" /> Registrar vehículo
          </Link>
        )}
      </div>
      {error && <p className="error">{error}</p>}

      <div className="tarjetas">
        {indicadores.map(({ rotulo, cifra, nota, Icono }) => (
          <div className="tarjeta indicador" key={rotulo}>
            <div className="rotulo">
              <Icono size={16} aria-hidden="true" /> {rotulo}
            </div>
            <div className="cifra">{vehiculos ? cifra : '…'}</div>
            <div className="nota">{vehiculos ? nota : ' '}</div>
          </div>
        ))}
      </div>

      <section className="seccion" aria-labelledby="pendientes">
        <header>
          <h2 id="pendientes">Pendientes</h2>
        </header>
        <div className="cuerpo">
          {conPendientes.length === 0 ? (
            <p className="vacio">
              <CheckCircle2 size={20} aria-hidden="true" /> Nada pendiente por ahora.
            </p>
          ) : (
            <ul className="pendientes">
              {conPendientes.map(({ clave, Icono, cuenta, texto, a, urgente }) => (
                <li key={clave}>
                  <Link to={a} className={urgente ? 'urgente' : undefined}>
                    <span className="pendiente-icono">
                      <Icono size={18} aria-hidden="true" />
                    </span>
                    <strong>{cuenta}</strong>
                    <span>{texto}</span>
                    <ArrowRight size={16} aria-hidden="true" className="pendiente-flecha" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="seccion" aria-labelledby="embudo">
        <header>
          <h2 id="embudo">De la subasta a la venta</h2>
        </header>
        <ol className="cuerpo embudo">
          {ETAPAS.map((e) => (
            <li key={e}>
              <Link to={`/vehiculos?estado=${e}`}>
                <strong>{vehiculos ? por(e) : '…'}</strong>
                <span>{ETIQUETAS_ESTADO[e]}</span>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section className="seccion" aria-labelledby="recientes">
        <header>
          <h2 id="recientes">Movimientos recientes</h2>
          <div className="relleno" />
          <Link className="boton chico" to="/vehiculos">
            Ver todos
          </Link>
        </header>
        {vehiculos && !vehiculos.length ? (
          <p className="vacio cuerpo">
            Aún no registras vehículos. Empieza con el VIN de tu primera compra.
          </p>
        ) : (
          <ul className="cuerpo recientes">
            {lista.slice(0, 8).map((v) => (
              <li key={v.id}>
                <Link to={`/vehiculos/${v.id}`}>
                  <span>
                    <strong>{nombreVehiculo(v)}</strong>
                    <small className="vin">{v.vin}</small>
                  </span>
                  <span className="etiqueta">{ETIQUETAS_ESTADO[v.estado] ?? v.estado}</span>
                  <small>{fechaCorta(v.actualizadoEn)}</small>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
